import queryString from 'query-string';
import window from 'window-or-global';

import { userInfoProvider } from '../../clients/user-info-provider';

import { type I18nDataLoaderConfig } from './type';

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_LOCALES: string[] = [
	'cs',
	'cs-CZ',
	'da',
	'da-DK',
	'de',
	'de-DE',
	'en',
	'en-GB',
	'en-US',
	'en-ZZ',
	'es',
	'es-ES',
	'et',
	'et-EE',
	'fi',
	'fi-FI',
	'fr',
	'fr-FR',
	'hu',
	'hu-HU',
	'is',
	'is-IS',
	'it',
	'it-IT',
	'ja',
	'ja-JP',
	'ko',
	'ko-KR',
	'nb',
	'nb-NO',
	'nl',
	'nl-NL',
	'pl',
	'pl-PL',
	'pt',
	'pt-BR',
	'pt-PT',
	'ro',
	'ro-RO',
	'ru',
	'ru-RU',
	'sk',
	'sk-SK',
	'sv',
	'sv-SE',
	'tr',
	'tr-TR',
	'th',
	'th-TH',
	'uk',
	'uk-UA',
	'vi',
	'vi-VN',
	'zh',
	'zh-TW',
	'zh-HK',
	'zh-ZH',
	'zh-CN',
];

// In URL, a tester can pass lang code or locale into URL, e.g `?locale=fr` or `?locale=cs-CZ`
const getLocaleFromUrl = (): string => {
	const { locale } = queryString.parse(window.location.search);
	const localeFromUrl = Array.isArray(locale) ? locale[0] : locale;
	return localeFromUrl as string;
};

function getSupportedLocale(locale?: string | null) {
	if (typeof locale !== 'string') {
		return null;
	}

	locale = locale.replace('_', '-');
	if (SUPPORTED_LOCALES.includes(locale)) {
		return locale;
	}
	// If we don't have the exact locale available, attempt to match just the language
	const languageCode = locale.substring(0, 2);
	if (SUPPORTED_LOCALES.includes(languageCode)) {
		return languageCode;
	}
}

export const getEffectiveLocale = async (predefinedLocale?: string) => {
	// locale that we can manually pass to intl provider
	const receivedLocale = getSupportedLocale(predefinedLocale);
	if (receivedLocale) {
		return receivedLocale;
	}

	// locale that we can pass to intl provider via `locale` GET param
	const localeInUrl = getSupportedLocale(getLocaleFromUrl());
	if (localeInUrl) {
		return localeInUrl;
	}

	let userLocale;
	try {
		userLocale = (await userInfoProvider.get()).locale;
	} catch (e) {
		userLocale = DEFAULT_LOCALE;
	}

	return getSupportedLocale(userLocale) || DEFAULT_LOCALE;
};

export const loadIntlData = (
	localesConfig: I18nDataLoaderConfig[],
	locale: string,
): Promise<any> => {
	const pickedConfig = localesConfig.find((config) => config.locales.includes(locale));

	const loader = pickedConfig?.loader;

	if (!loader) {
		return Promise.resolve(null);
	}

	return new Promise((resolve, reject) => {
		// Fails if we haven't loaded/resolved after 30 seconds
		const rejectTimer = setTimeout(reject, 30000);
		void loader().then((res) => {
			clearTimeout(rejectTimer);

			// depending on a builder and config used in a project the result of dynamic imports could be behind `default`
			// this way we'll cover both cases
			resolve((res && res.default) || res);
		});
	});
};
